import { forwardRef, useId } from "react";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/utils";
import RequiredMark from "./RequiredMark";

const inputVariants = cva(
  "w-full rounded-xl border bg-text-dark/8 px-4 text-bold text-text-dark placeholder:text-text-dark/40 outline-none transition-all duration-200 focus:ring-2 disabled:opacity-50 disabled:pointer-events-none",
  {
    variants: {
      variant: {
        default:
          "border-white/15 focus:border-emerald-500 focus:ring-emerald-500/30",
        error: "border-red-500/60 focus:border-red-500 focus:ring-red-500/30",
      },
      size: {
        md: "h-11",
        lg: "h-[3.25rem] text-base",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "lg",
    },
  }
);

const Input = forwardRef(function Input(
  { label, error, hint, className, variant, size, id, ...props },
  ref
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <label
          htmlFor={inputId}
          className="text-lg font-medium text-text-dark/80"
        >
          {label}
          {props.required && <RequiredMark />}
        </label>
      )}

      <input
        id={inputId}
        ref={ref}
        aria-required={props.required || undefined}
        aria-invalid={error ? true : undefined}
        className={cn(
          inputVariants({ variant: error ? "error" : variant, size }),
          className
        )}
        {...props}
      />

      {hint && !error && <p className="text-xs text-text-dark/50">{hint}</p>}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
});

export default Input;

